import { useEffect, useState } from "react";

/**
 * Canonical responsive breakpoints (px). Custom properties can't be used
 * inside CSS `@media` conditions, so the literal px also live in the
 * `@media` rules in benthic-theme.css; these JS constants mirror them.
 *
 * phone  < 640  →  useIsMobile()       →  @media (max-width: 639px)
 * nav    ≤ 768  →  useIsNavCollapsed()  →  @media (max-width: 768px)
 */
export const BP_MOBILE = 640;
export const BP_NAV = 768;

/**
 * Pure, side-effect-isolated core: evaluate a media query NOW and subscribe
 * to its `change` events. Returns the current `matches` plus an `unsubscribe`
 * cleanup. SSR-guarded (no `window` → matches=false, no-op unsubscribe),
 * mirroring the guard in lib/theme.ts:7. This is the unit-tested surface;
 * `useMediaQuery` is a thin React wrapper over it.
 */
export function evaluateAndSubscribe(
  query: string,
  onChange: (matches: boolean) => void,
): { matches: boolean; unsubscribe: () => void } {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return { matches: false, unsubscribe: () => {} };
  }
  const mql = window.matchMedia(query);
  const listener = (e: MediaQueryListEvent) => onChange(e.matches);
  mql.addEventListener("change", listener);
  return {
    matches: mql.matches,
    unsubscribe: () => mql.removeEventListener("change", listener),
  };
}

/**
 * Reactive `matchMedia` hook. Re-renders when the query's match state flips.
 * SSR-safe via evaluateAndSubscribe.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(
    () => evaluateAndSubscribe(query, () => {}).matches,
  );

  useEffect(() => {
    // Re-evaluate on mount (state may be stale if the query string changed)
    // and subscribe for subsequent flips.
    const { matches: current, unsubscribe } = evaluateAndSubscribe(
      query,
      setMatches,
    );
    setMatches(current);
    return unsubscribe;
  }, [query]);

  return matches;
}

/** Phone: below the 640px boundary. Mirrors @media (max-width: 639px). */
export function useIsMobile(): boolean {
  return useMediaQuery("(max-width: 639px)");
}

/** Nav-collapse: a portrait tablet still can't afford the 240px rail. */
export function useIsNavCollapsed(): boolean {
  return useMediaQuery("(max-width: 768px)");
}
